import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { Skeleton } from '../../components/ui/skeleton';
import { ArrowLeft, Plus, Gavel, Calendar, Building2, Loader2, Edit, Trash2, Timer, Archive, RotateCcw, Users, TrendingUp, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import CreateSaleDialog from '../../components/admin/CreateSaleDialog';
import { useConfirm } from '../../components/common/ConfirmDialog';

const ManageSalesPage = () => {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [showClosed, setShowClosed] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [closingId, setClosingId] = useState(null);
  const [reopeningId, setReopeningId] = useState(null);
  const [statsDialogSale, setStatsDialogSale] = useState(null);
  const confirm = useConfirm();

  const { data: sales = [], isLoading: loading } = useQuery({
    queryKey: QK.salesManagement(),
    queryFn: () => api.get('/sales-management').then(r => r.data),
    onError: () => toast.error('Failed to load sales'),
  });

  const { data: allSalesIncClosed = [] } = useQuery({
    queryKey: QK.salesManagementAll(),
    queryFn: () => api.get('/sales-management?include_closed=true').then(r => r.data),
    enabled: showClosed,
  });
  const closedSales = allSalesIncClosed.filter(s => s.is_closed).sort((a, b) => {
    const da = (a.sale_start_date || '').substring(0, 10);
    const db_ = (b.sale_start_date || '').substring(0, 10);
    return db_ < da ? -1 : db_ > da ? 1 : 0;
  });

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: QK.salesManagement() });
    qc.invalidateQueries({ queryKey: QK.salesManagementAll() });
  };

  const handleSaleCreated = async () => {
    setDialogOpen(false);
    setEditingSale(null);
    invalidate();
  };

  const handleEdit = (sale) => {
    setEditingSale(sale);
    setDialogOpen(true);
  };

  const handleDelete = async (saleId) => {
    if (!await confirm({ title: 'Delete this sale? This will remove it for all tenants.', destructive: true })) return;
    try {
      await api.delete(`/sales-management/${saleId}`);
      toast.success('Sale deleted');
      invalidate();
    } catch (error) {
      toast.error('Failed to delete sale');
    }
  };

  const handleClose = async (saleId, saleName) => {
    if (!await confirm({ title: `Close "${saleName}"? It will stop syncing and be hidden from tenant views. You can reopen it at any time.`, destructive: true })) return;
    setClosingId(saleId);
    try {
      await api.post(`/sales-management/${saleId}/close`);
      toast.success('Sale closed and archived');
      invalidate();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to close sale');
    } finally {
      setClosingId(null);
    }
  };

  const handleReopen = async (saleId, saleName) => {
    setReopeningId(saleId);
    try {
      await api.post(`/sales-management/${saleId}/reopen`);
      toast.success(`"${saleName}" reopened`);
      invalidate();
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to reopen sale');
    } finally {
      setReopeningId(null);
    }
  };

  const { data: statsData, isLoading: statsLoading } = useQuery({
    queryKey: ['sale-tenant-stats', statsDialogSale?.id],
    queryFn: () => api.get(`/sales-management/${statsDialogSale.id}/tenant-stats`).then(r => r.data),
    enabled: !!statsDialogSale?.id,
    staleTime: 60_000,
  });

  // Pre-fetch lightweight counts for all active sales so SaleRow badges show real numbers
  const { data: allStats = {} } = useQuery({
    queryKey: ['sale-tenant-stats-all'],
    queryFn: async () => {
      const results = {};
      await Promise.all((sales || []).map(async sale => {
        try {
          const r = await api.get(`/sales-management/${sale.id}/tenant-stats`);
          results[sale.id] = r.data;
        } catch { /* ignore per-sale errors */ }
      }));
      return results;
    },
    enabled: sales.length > 0,
    staleTime: 120_000,
  });

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  // ── Tenant Engagement Dialog ───────────────────────────────────────────────
  const TenantEngagementDialog = () => (
    <Dialog open={!!statsDialogSale} onOpenChange={open => { if (!open) setStatsDialogSale(null); }}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Users className="w-4 h-4 text-primary" />
            Tenant Engagement — {statsDialogSale?.sale_name}
          </DialogTitle>
        </DialogHeader>

        {statsLoading ? (
          <div className="space-y-3 py-4">
            {[1,2,3].map(i => <Skeleton key={i} className="h-14 w-full" />)}
          </div>
        ) : !statsData || statsData.total_tenants_active === 0 ? (
          <div className="text-center py-10 text-muted-foreground">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-20" />
            <p className="text-sm">No tenants have entered opportunities for this sale yet.</p>
          </div>
        ) : (
          <div className="space-y-4 py-2">
            {/* Summary row */}
            <div className="flex gap-3">
              <div className="flex-1 rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold text-primary">{statsData.total_tenants_active}</p>
                <p className="text-xs text-muted-foreground mt-0.5">Tenants Active</p>
              </div>
              <div className="flex-1 rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold text-primary">
                  {statsData.tenants.reduce((sum, t) => sum + t.opportunity_count, 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Total Opportunities</p>
              </div>
              <div className="flex-1 rounded-lg border p-3 text-center">
                <p className="text-2xl font-bold text-primary">
                  {statsData.tenants.reduce((sum, t) => sum + t.horse_count, 0)}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">Unique Horses</p>
              </div>
            </div>

            {/* Per-tenant table */}
            <div className="rounded-lg border overflow-hidden">
              <div className="grid grid-cols-3 gap-0 bg-muted/50 px-4 py-2 text-xs font-medium text-muted-foreground uppercase tracking-wide">
                <span>Tenant</span>
                <span className="text-center">Opportunities</span>
                <span className="text-center">Horses</span>
              </div>
              <div className="divide-y">
                {statsData.tenants.map(t => (
                  <div key={t.tenant_id} className="grid grid-cols-3 gap-0 px-4 py-3 items-center hover:bg-muted/30">
                    <span className="text-sm font-medium truncate pr-2">{t.tenant_name}</span>
                    <span className="text-center text-sm">{t.opportunity_count}</span>
                    <span className="text-center text-sm">{t.horse_count}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );

  // ── SaleRow ─────────────────────────────────────────────────────────────────
  const SaleRow = ({ sale, isClosed = false }) => (
    <div
      key={sale.id}
      className={`flex items-center justify-between p-4 border rounded hover:bg-muted/30 ${isClosed ? 'opacity-70' : ''}`}
    >
      <div className="flex-1">
        <div className="flex items-center gap-3 mb-1">
          <Gavel className="w-5 h-5 text-primary shrink-0" />
          <p className="font-semibold">{sale.sale_name}</p>
          {sale.is_system_wide ? (
            <Badge className="bg-blue-600">System-Wide</Badge>
          ) : (
            <Badge variant="outline">Tenant-Specific</Badge>
          )}
          {isClosed && (
            <Badge variant="outline" className="border-orange-400 text-orange-600">Closed</Badge>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span className="flex items-center gap-1">
            <Calendar className="w-3 h-3" />
            {formatDate(sale.sale_start_date)}
            {sale.sale_end_date && sale.sale_start_date !== sale.sale_end_date &&
              <> – {formatDate(sale.sale_end_date)}</>}
          </span>
          {sale.sale_company && (
            <span className="flex items-center gap-1">
              <Building2 className="w-3 h-3" /> {sale.sale_company}
            </span>
          )}
          {sale.location && <span>{sale.location}</span>}
          {!sale.is_system_wide && sale.tenant_name && (
            <span className="flex items-center gap-1">
              <Building2 className="w-3 h-3" /> {sale.tenant_name}
            </span>
          )}
          {isClosed && sale.closed_at && (
            <span className="text-orange-600/70">
              Closed {formatDate(sale.closed_at)}
            </span>
          )}
        </div>
        {sale.is_undertack_show && (sale.under_tack_start_date || sale.under_tack_end_date) && (
          <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
            <Timer className="w-3.5 h-3.5 shrink-0" />
            <span>
              Undertack:{' '}
              {sale.under_tack_start_date && formatDate(sale.under_tack_start_date)}
              {sale.under_tack_start_date && sale.under_tack_end_date && ' – '}
              {sale.under_tack_end_date && formatDate(sale.under_tack_end_date)}
            </span>
          </div>
        )}

        {/* Tenant engagement stats — clickable to open detail */}
        {!isClosed && (
          <button
            className="flex items-center gap-3 mt-2 text-xs text-muted-foreground hover:text-primary transition-colors group"
            onClick={(e) => { e.stopPropagation(); setStatsDialogSale(sale); }}
            data-testid={`tenant-stats-${sale.id}`}
          >
            <span className="flex items-center gap-1">
              <Users className="w-3 h-3" />
              <span className="font-medium text-foreground group-hover:text-primary">
                {allStats[sale.id]?.total_tenants_active ?? '—'}
              </span>
              <span>tenants active</span>
            </span>
            <span className="flex items-center gap-1">
              <TrendingUp className="w-3 h-3" />
              <span className="font-medium text-foreground group-hover:text-primary">
                {allStats[sale.id]?.tenants?.reduce((s, t) => s + t.opportunity_count, 0) ?? '—'}
              </span>
              <span>opportunities</span>
            </span>
            <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
          </button>
        )}
      </div>
      <div className="flex gap-1 shrink-0 ml-2">
        {isClosed ? (
          <Button
            variant="ghost" size="sm"
            onClick={() => handleReopen(sale.id, sale.sale_name)}
            disabled={reopeningId === sale.id}
            title="Reopen sale"
            data-testid={`reopen-sale-${sale.id}`}
          >
            {reopeningId === sale.id
              ? <Loader2 className="w-4 h-4 animate-spin" />
              : <RotateCcw className="w-4 h-4" />}
          </Button>
        ) : (
          <>
            <Button variant="ghost" size="sm" onClick={() => handleEdit(sale)} title="Edit sale">
              <Edit className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost" size="sm"
              onClick={() => handleClose(sale.id, sale.sale_name)}
              disabled={closingId === sale.id}
              title="Close &amp; archive sale"
              data-testid={`close-sale-${sale.id}`}
            >
              {closingId === sale.id
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Archive className="w-4 h-4" />}
            </Button>
            <Button
              variant="ghost" size="sm"
              onClick={() => handleDelete(sale.id)}
              className="text-destructive"
              title="Delete sale"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </>
        )}
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="bg-primary text-white px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <Button
            variant="ghost" size="icon"
            onClick={() => navigate('/admin/system')}
            className="text-white hover:bg-white/20"
          >
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="font-serif text-xl font-semibold">Manage Sales</h1>
            <p className="text-xs text-white/70">Create and manage sales for all tenants</p>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6">
        <div className="space-y-6">
          {/* Actions */}
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-semibold">Sales & Auctions</h2>
              <p className="text-sm text-muted-foreground">System-wide and tenant-specific sales</p>
            </div>
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-2">
                <Switch
                  id="show-closed"
                  checked={showClosed}
                  onCheckedChange={setShowClosed}
                  data-testid="show-closed-toggle"
                />
                <Label htmlFor="show-closed" className="text-sm text-muted-foreground cursor-pointer">
                  Show Closed
                </Label>
              </div>
              <Button onClick={() => setDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" /> Create Sale
              </Button>
            </div>
          </div>

          {/* Active Sales */}
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Active Sales</CardTitle>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="flex justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-primary" />
                </div>
              ) : sales.length === 0 ? (
                <div className="text-center py-12 text-muted-foreground">
                  <Gavel className="w-12 h-12 mx-auto mb-3 opacity-20" />
                  <p>No active sales</p>
                  <Button onClick={() => setDialogOpen(true)} variant="outline" className="mt-4">
                    <Plus className="w-4 h-4 mr-2" /> Create First Sale
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  {sales.map(sale => <SaleRow key={sale.id} sale={sale} />)}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Closed Sales (conditional) */}
          {showClosed && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Archive className="w-4 h-4 text-muted-foreground" />
                  Closed Sales
                </CardTitle>
              </CardHeader>
              <CardContent>
                {closedSales.length === 0 ? (
                  <p className="text-center py-8 text-muted-foreground text-sm">No closed sales.</p>
                ) : (
                  <div className="space-y-3">
                    {closedSales.map(sale => <SaleRow key={sale.id} sale={sale} isClosed />)}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>

        <TenantEngagementDialog />

        {/* Create/Edit Sale Dialog */}
        <CreateSaleDialog
          open={dialogOpen}
          onClose={() => { setDialogOpen(false); setEditingSale(null); }}
          onSuccess={handleSaleCreated}
          sale={editingSale}
        />
      </main>
    </div>
  );
};

export default ManageSalesPage;
